import { sha1 } from 'crypto-hash';
import { createContext, type ComponentChildren } from 'preact';
import { useContext } from 'preact/hooks';
import {
  useComputed,
  useSignal,
  useSignalEffect,
  type ReadonlySignal
} from '@preact/signals';
import type { CategoryCode } from '@/domain/Category';
import type { Product } from '@/domain/Product';
import type { PromotionCardDefinition } from '../../config';

const CardModalIdContext = createContext<any | undefined>(undefined);

export type CardModalIdProviderProps = {
  children: ComponentChildren;
  collectionCode: CategoryCode;
};

export function CardModalIdProvider({
  children,
  collectionCode
}: CardModalIdProviderProps) {
  return (
    <CardModalIdContext.Provider value={collectionCode}>
      {children}
    </CardModalIdContext.Provider>
  );
}

export default function useCardModalId<StateModalProp>(
  modalProp: StateModalProp,
  cardId: Product['id'] | PromotionCardDefinition['promotionId']
): ReadonlySignal<StateModalProp> {
  const collectionCode = useContext(CardModalIdContext);

  if (!collectionCode) {
    throw new Error('useCardModalId must be used within CardModalIdProvider');
  }

  const hash = useSignal('');

  useSignalEffect(() => {
    (async () => {
      hash.value = await sha1(`${collectionCode}${cardId}`);
    })();
  });

  return useComputed(() => `${modalProp}${hash.value}` as StateModalProp);
}
